import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  StyleSheet,
  Animated,
} from "react-native";

import LS from "../../../utils/localStorageUtils";
import i18n from "../../../i18n/i18n";
import CheckboxIcon from "../../../assets/login/checkbox.png";

type LangOption = {
  code: string;
  label: string;
  flag?: any;
};

const ENGLISH_LANG_NAMES: Record<string, string> = {
  en: "English",
  bg: "Bulgarian",
  fr: "French",
  ru: "Russian",
  tr: "Turkish",
};

type LanguagePickerProps = {
  value?: string;
  options: LangOption[];
  onChange?: (code: string) => void;
  /** Extra style for the outer wrapper View (controls zIndex, etc.) */
  containerStyle?: any;
  buttonStyle?: any;
  labelStyle?: any;
  flagStyle?: any;
  caretIcon?: any;
  caretIconOpen?: any;
  menuStyle?: any;
  menuItemActiveStyle?: any;
  menuPlacement?: "above" | "below";
  showCurrentEnglishLabel?: boolean;
  showMenuEnglishLabel?: boolean;
};

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
  value = "en",
  options,
  onChange,
  containerStyle,
  buttonStyle,
  labelStyle,
  flagStyle,
  caretIcon,
  caretIconOpen,
  menuStyle,
  menuItemActiveStyle,
  menuPlacement = "above",
  showCurrentEnglishLabel = true,
  showMenuEnglishLabel = true,
}) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const menuAnim = useRef(new Animated.Value(0)).current;

  const current = useMemo(
    () => options.find((o) => o.code === value) || options[0],
    [value, options],
  );

  const currentEnglishLabel =
    (current && ENGLISH_LANG_NAMES[current.code]) || current?.label;

  const handleSelect = (code: string) => {
    setOpen(false);

    i18n.changeLanguage(code).catch(() => {});

    LS.set("lang", code);

    onChange?.(code);
  };

  useEffect(() => {
    if (open) {
      setVisible(true);
      menuAnim.setValue(0);
      Animated.timing(menuAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  }, [open, menuAnim]);

  useEffect(() => {
    if (!open && visible) {
      Animated.timing(menuAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start(() => setVisible(false));
    }
  }, [open, visible, menuAnim]);

  return (
    <View style={[{ position: "relative" }, containerStyle]}>
      <TouchableOpacity
        onPress={() => setOpen((prev) => !prev)}
        activeOpacity={0.8}
        style={[styles.langButton, buttonStyle]}
      >
        <View style={styles.buttonLeft}>
          {current?.flag ? (
            <Image source={current.flag} style={[styles.flag, flagStyle]} />
          ) : null}
          <Text style={[styles.langLabel, labelStyle]} numberOfLines={1}>
            {current?.label}
          </Text>
        </View>
        <View style={styles.buttonRight}>
          {showCurrentEnglishLabel && (
            <Text style={[styles.translateLabel, labelStyle]} numberOfLines={1}>
              {currentEnglishLabel}
            </Text>
          )}
          {caretIcon ? (
            <Animated.Image
              source={caretIcon}
              style={[
                styles.caret,
                {
                  transform: [
                    {
                      rotate: menuAnim.interpolate({
                        inputRange: [0, 1],
                        outputRange: ["0deg", "180deg"],
                      }),
                    },
                  ],
                },
              ]}
            />
          ) : null}
        </View>
      </TouchableOpacity>

      {visible && (
        <View
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            ...(menuPlacement === "above" ? { bottom: 42 } : { top: 48 }),
            zIndex: 1000,
          }}
        >
          <Animated.View
            style={[
              styles.menu,
              menuStyle,
              {
                opacity: menuAnim,
                transform: [
                  {
                    scaleY: menuAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0.9, 1],
                    }),
                  },
                ],
              },
            ]}
          >
            {options.map((item) => (
              <TouchableOpacity
                key={item.code}
                style={[
                  styles.menuItem,
                  item.code === value && menuItemActiveStyle,
                ]}
                onPress={() => handleSelect(item.code)}
              >
                {item.flag ? (
                  <Image source={item.flag} style={styles.menuFlag} />
                ) : null}

                <View style={styles.menuTextContainer}>
                  <Text
                    style={[
                      styles.menuText,
                      item.code === value && styles.menuTextActive,
                    ]}
                    numberOfLines={1}
                  >
                    {item.label}
                  </Text>
                  {showMenuEnglishLabel && (
                    <Text
                      style={[styles.translateLabel, labelStyle]}
                      numberOfLines={1}
                    >
                      {ENGLISH_LANG_NAMES[item.code] || item.label}
                    </Text>
                  )}
                </View>

                <View style={styles.menuCheckboxWrapper}>
                  {item.code === value && (
                    <Image
                      source={CheckboxIcon}
                      style={styles.menuCheckbox}
                      resizeMode="contain"
                    />
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </Animated.View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  langButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0f2440",
    borderRadius: 6,
    paddingHorizontal: 12,
    height: 48,
    minWidth: 150,
    justifyContent: "space-between",
  },
  flag: { width: 26, height: 26, marginRight: 0 },
  langLabel: { color: "#fff", fontSize: 16, marginLeft: 10, maxWidth: 80 },
  translateLabel: {
    color: "#a86f6f",
    fontSize: 16,
    marginRight: 6,
    maxWidth: 100,
  },
  caret: { width: 16, height: 16, marginLeft: 6 },
  buttonLeft: {
    flexDirection: "row",
    alignItems: "center",
    flexShrink: 1,
  },
  buttonRight: {
    flexDirection: "row",
    alignItems: "center",
  },
  menu: {
    width: 220,
    maxHeight: 360,
    backgroundColor: "#0f2440",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#00f0ff",
    paddingVertical: 8,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginHorizontal: 4,
    justifyContent: "space-between",
  },
  menuFlag: { width: 26, height: 26, marginRight: 10 },
  menuText: { color: "#cdd6e6", fontSize: 16 },
  menuTextActive: { color: "#FFEAA0", fontWeight: "700" },
  menuTextContainer: {
    flex: 1,
    marginRight: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  menuCheckbox: {
    width: 20,
    height: 20,
  },
  menuCheckboxWrapper: {
    width: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});
